/** Pomii și boscheții orașului: în parcurile și pe verdele din OSM, în jurul
 *  lotului, ferit de clădiri, străzi, căi ferate, apă și de lot. Pe zone, ca
 *  pe lot (v. `plan/vegetatie.ts`): pâlcuri dese cu tufăriș, între ele pomi
 *  răsleți, boscheți numai lângă pomi.
 *
 *  Numai până la `RAZA_VEGETATIE_M` și cel mult `MAX_POMI` / `MAX_TUFISURI`,
 *  cei mai apropiați de lot întâi: frunzișul costă în pixeli (v. `copaci3d.ts`),
 *  iar departe pomii ar fi doar puncte. */

import type { CopacSit, TufisSit } from "../plan/copaci3d";
import { campDensitate, Obstacole, pastreazaPomi, presara, tufisuriLaPomi } from "../plan/vegetatie";
import type { Oras3 } from "./tipuri";

export const RAZA_VEGETATIE_M = 700;
export const MAX_POMI = 900;
export const MAX_TUFISURI = 1200;

export function vegetatieOras(o: Oras3): { copaci: CopacSit[]; tufisuri: TufisSit[] } {
  const [cx, cy] = o.centru;
  const raza = RAZA_VEGETATIE_M;
  const aproape = (inel: number[][]) => inel.some((p) => Math.hypot(p[0] - cx, p[1] - cy) < raza + 150);
  const verde = o.verde.filter((s) => aproape(s.inele[0]))
    .map((s) => ({ poly: s.inele[0], holes: s.inele.slice(1) }));
  if (!verde.length) return { copaci: [], tufisuri: [] };

  // de două ori: pomii se țin departe unul de altul (coroana), boscheții
  // stau sub coroane și ocolesc doar trunchiul
  const obstacole = () => {
    const ob = new Obstacole(30);
    // ca pe lot (`POM_LANGA_CLADIRE`): nimic la mai puțin de 2 m de o clădire
    for (const c of o.cladiri) if (aproape(c.inele[0])) ob.inel(c.inele[0], 2.0);
    for (const s of [...o.strazi, ...o.pietonale, ...o.caiFerate]) if (aproape(s.inele[0])) ob.inel(s.inele[0], 0.4);
    for (const s of o.apa) if (aproape(s.inele[0])) ob.inel(s.inele[0], 0.5);
    if (o.lot.length >= 3) ob.inel(o.lot, 1.0);
    return ob;
  };
  const obst = obstacole();

  const inRaza = (p: CopacSit) => Math.hypot(p.x - cx, p.y - cy) <= raza;
  // pâlcuri de ~60 m
  const camp = (x: number, y: number) => campDensitate(x, y, 53, 60);
  const copaci = presara(verde, obst, { pas: 7, densitate: 1, camp: pastreazaPomi(camp),
                                        rMin: 2.0, rMax: 3.6, sare: 51,
                                        max: MAX_POMI, centru: [cx, cy] }).filter(inRaza);
  const obstTufis = obstacole();
  for (const c of copaci) obstTufis.cerc(c.x, c.y, 0.35);
  const tufisuri = tufisuriLaPomi(copaci, obstTufis, camp).slice(0, MAX_TUFISURI);
  return { copaci, tufisuri };
}
